/**
 * Cookie Consent Banner Module
 * Displays the initial cookie consent prompt
 */
import type { CookieManager } from '../services/cookieManager';

export class CookieConsentBanner {
    cookieManager: CookieManager;
    banner: HTMLElement;
    isVisible: boolean;

    constructor(cookieManager: CookieManager) {
        this.cookieManager = cookieManager;
        this.banner = document.createElement('div');
        this.isVisible = false;
        
        this.createBanner();
        this.bindEvents();
    }

    /**
     * Create the cookie consent banner HTML
     */
    createBanner(): void {
        this.banner.className = 'cookie-consent-banner';
        this.banner.id = 'cookieConsentBanner';
        
        this.banner.innerHTML = `
            <div class="cookie-modal-overlay"></div>
            <div class="cookie-banner-modal-container">
                <div class="cookie-banner-header">
                    <div class="cookie-banner-header-text">
                        <div class="cookie-banner-title">
                            <div class="cookie-banner-icon">
                                <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                                    <circle cx="8" cy="8" r="6.67" stroke="#6B7280" stroke-width="1.33"/>
                                    <circle cx="5.67" cy="5.67" r="0.005" stroke="#6B7280" stroke-width="1.33"/>
                                    <circle cx="10.67" cy="10.33" r="0.005" stroke="#6B7280" stroke-width="1.33"/>
                                    <circle cx="8" cy="8" r="0.005" stroke="#6B7280" stroke-width="1.33"/>
                                    <circle cx="7.33" cy="11.33" r="0.005" stroke="#6B7280" stroke-width="1.33"/>
                                    <circle cx="4.67" cy="9.33" r="0.005" stroke="#6B7280" stroke-width="1.33"/>
                                </svg>
                            </div>
                            <span class="cookie-banner-title-text">Cookie Notice</span>
                        </div>
                        <p class="cookie-banner-subtitle">Manage your cookie preferences</p>
                    </div>
                </div>
                <p class="cookie-banner-description">
                    We use essential cookies for functionality and analytics to improve your experience.
                </p>
                <div class="cookie-banner-actions">
                    <div class="cookie-banner-main-actions">
                        <button class="cookie-reject-all-btn" id="cookieRejectAllBtn" data-testid="cookie-reject-all">
                            Reject All
                        </button>
                        <button class="cookie-accept-all-btn" id="cookieAcceptAllBtn" data-testid="cookie-accept-all">
                            Accept All
                        </button>
                    </div>
                    <button class="cookie-settings-link" id="cookieSettingsLink">
                        Cookie Settings
                    </button>
                </div>
            </div>
        `;

        // Add banner to document body
        document.body.appendChild(this.banner);
    }

    /**
     * Bind event listeners
     */
    bindEvents(): void {
        const acceptAllBtn = this.banner.querySelector('#cookieAcceptAllBtn');
        const rejectAllBtn = this.banner.querySelector('#cookieRejectAllBtn');
        const settingsLink = this.banner.querySelector('#cookieSettingsLink');

        acceptAllBtn?.addEventListener('click', () => {
            this.handleAcceptAll();
        });

        rejectAllBtn?.addEventListener('click', () => {
            this.handleRejectAll();
        });

        settingsLink?.addEventListener('click', () => {
            this.handleSettingsClick();
        });
    }

    /**
     * Handle accept all button click
     */
    handleAcceptAll(): void {
        this.cookieManager.acceptAll();
        this.hide();
    }

    /**
     * Handle reject all button click
     */
    handleRejectAll(): void {
        this.cookieManager.rejectAll();
        this.hide();
    }

    /**
     * Handle settings link click
     */
    handleSettingsClick(): void {
        // Hide the banner and show the expanded settings
        this.hide();
        // Dispatch event for cookie settings modal to listen to
        window.dispatchEvent(new CustomEvent('showCookieSettings', {
            detail: { fromBanner: true }
        }));
    }

    /**
     * Show the banner
     */
    show(): void {
        if (!this.isVisible && !this.cookieManager.hasConsented()) {
            this.banner.classList.add('cookie-banner-visible');
            this.isVisible = true;
            
            // Add backdrop to prevent interaction with page
            document.body.classList.add('cookie-banner-active');
        }
    }

    /**
     * Hide the banner
     */
    hide(): void {
        if (this.isVisible) {
            this.banner.classList.remove('cookie-banner-visible');
            this.isVisible = false;
            
            // Remove backdrop
            document.body.classList.remove('cookie-banner-active');
        }
    }

    /**
     * Check if banner should be shown on page load
     */
    checkAndShow(): void {
        if (!this.cookieManager.hasConsented()) {
            // Small delay to ensure page is loaded
            setTimeout(() => {
                this.show();
            }, 500);
        }
    }

    /**
     * Destroy the banner
     */
    destroy(): void {
        if (this.banner && this.banner.parentNode) {
            this.banner.parentNode.removeChild(this.banner);
        }
        this.isVisible = false;
        document.body.classList.remove('cookie-banner-active');
    }
}
